import { useState, useEffect } from "react"

import { VertesiaClient } from "@vertesia/client"
import { ChevronRight } from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { cn } from "@lib/utils"

import { useUserSession } from "@session/UserSession"

import { Checkbox } from "@ui/checkbox"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@ui/collapsible"
import { Input } from "@ui/input"

interface FilterSectionProps {
    section: string
    placeholder: string
    Icon?: LucideIcon
    items: any[]
    onSelection?: (selection: any) => void
    onClear: number
    multiple?: boolean
    transform?: (value: string, client: VertesiaClient, callback: (result: string) => void) => void
}
export function FilterSection({ section, placeholder, Icon, items, onSelection, onClear, multiple = false, transform }: Readonly<FilterSectionProps>) {
    const { client } = useUserSession()

    const [searchTerm, setSearchTerm] = useState("")
    const [selectedItems, setSelectedItems] = useState<any[]>([])
    const [transformedItems, setTransformedItems] = useState(items)

    useEffect(() => {
        if (transform) {
            const updatedItems = [...items]
            let completedTransforms = 0

            for (let i = 0; i < items.length; i++) {
                const item = items[i]

                transform(item.name, client, (transformedName) => {
                    updatedItems[i] = { ...item, name: transformedName }
                    completedTransforms++

                    if (completedTransforms === items.length) {
                        setTransformedItems(updatedItems)
                    }
                })
            }
        } else {
            setTransformedItems(items)
        }
    }, [items])

    const filteredItems = transformedItems.filter((item) => item?.name.toLowerCase().includes(searchTerm.toLowerCase()))

    const handleOnSelection = (id: any) => {
        let newSelectedItems

        if (multiple) {
            if (selectedItems.includes(id)) {
                newSelectedItems = selectedItems.filter((item) => item !== id)
            } else {
                newSelectedItems = [...selectedItems, id]
            }
        } else {
            newSelectedItems = selectedItems.includes(id) ? [] : [id]
        }

        setSelectedItems(newSelectedItems)
        onSelection?.(multiple ? newSelectedItems : newSelectedItems[0] || undefined)
    }

    const isItemSelected = (id: any) => {
        return selectedItems.includes(id)
    }

    useEffect(() => {
        setSelectedItems([])
    }, [onClear])

    return (
        <div className={cn("relative flex w-[calc(var(--filterbar-width)-32px)] flex-col p-2 border-b border-sidebar-border")}>
            <Collapsible defaultOpen={false} className="group/collapsible">
                <CollapsibleTrigger className={cn("group/label w-full text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", "flex h-8 shrink-0 items-center rounded-md px-2 text-sm font-medium outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 hover:cursor-pointer mb-1")}>
                    {Icon && <Icon className="mr-2" />}
                    {section} <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="flex flex-col gap-2">
                        {items.length > 5 && <Input placeholder={placeholder} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} className="bg-white shadow-none hover:shadow-sm" />}
                        {filteredItems.map((item, index) => (
                            <div className="peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left outline-none ring-sidebar-ring transition-[width,height,padding] focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-8 text-sm hover:cursor-pointer" key={`${crypto.randomUUID()}_${section}`}>
                                <Checkbox id={`item-${index}`} checked={isItemSelected(item._id)} onCheckedChange={() => handleOnSelection(item._id)} />
                                <label htmlFor={`item-${index}`} className="w-full min-w-0" aria-label={`${item.name} (${item.count} items)`}>
                                    <div className="w-full flex flex-row items-center justify-between hover:cursor-pointer min-w-0">
                                        <span className="truncate flex-1 min-w-0">{item.name}</span>
                                        <span className="ml-2 flex-shrink-0">({item.count})</span>
                                    </div>
                                </label>
                            </div>
                        ))}
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    )
}
